import React, { memo, useCallback, useMemo, useState, useEffect, forwardRef } from 'react';
import { usePrevious } from 'js/hooks/usePrevious';
import { addAllowedParamsToSrc } from 'js/utils/url.util';

import * as helpers from './helpers';
import * as Types from './types';

import style from './Image.scss';

export const ImageBare = forwardRef<HTMLImageElement, Types.TImageProps>(({
    alt,
    className,
    onError,
    onSourceChange,
    isRemoveOnError,
    src,
    draggable = false,
    ...props
}, ref) => {
    const prevSrc = usePrevious(src);
    const [imgIndex, setImgIndex] = useState(0);
    const [isError, setIsError] = useState(false);
    const imgSources = useMemo(() => Array.isArray(src) ? src : [src], [src]);
    const imgSource = imgSources[imgIndex];

    useEffect(() => {
        if (prevSrc !== null && !helpers.areSourcesEqual(prevSrc, src)) {
            setImgIndex(0);
        }
    }, [prevSrc, src]);

    useEffect(() => {
        onSourceChange && onSourceChange(imgSource);
    }, [onSourceChange, imgSource]);

    const onImageError = useCallback((event: React.SyntheticEvent<HTMLImageElement>): void => {
        onError && onError(event);

        if (imgIndex >= imgSources.length - 1) {
            setIsError(true);

            return;
        }

        setImgIndex(imgIndex + 1);
    }, [onError, imgSources, imgIndex, setImgIndex, setIsError]);

    if (isRemoveOnError && isError) {
        return null;
    }

    return (
        <img
            ref={ref}
            alt={alt}
            draggable={draggable}
            onError={onImageError}
            src={addAllowedParamsToSrc(imgSource)}
            className={`${style.image} ${className}`}
            {...props}
        />
    );
});

export const Image = memo(ImageBare);
